'use client';

import React, { useState, useEffect } from 'react';
import MissionStatement from './MissionStatement';
import CareerTimeline from './CareerTimeline';
import CoreValues from './CoreValues';
import CallToAction from './CallToAction';
import { careerJourneyService, type CareerMilestone } from '@/services/careerJourneyService';



interface PhilosophyPillar {
  icon: string;
  title: string;
  description: string;
  principles: string[];
}

interface TimelineEvent {
  year: string;
  title: string;
  organization: string;
  description: string;
  achievements: string[];
  type: 'milestone' | 'transition' | 'achievement';
}

interface CoreValue {
  icon: string;
  title: string;
  description: string;
  color: string;
}

interface Endorsement {
  skill: string;
  endorsers: {
    name: string;
    title: string;
    image: string;
    alt: string;
  }[];
  count: number;
}

const AboutInteractive: React.FC = () => {
  const [expandedSkill, setExpandedSkill] = useState<string | null>(null);
  const [timelineEvents, setTimelineEvents] = useState<TimelineEvent[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadCareerMilestones();
  }, []);

  const loadCareerMilestones = async () => {
    try {
      setLoading(true);
      const { data, error } = await careerJourneyService.getAllPublicMilestones();
      
      if (!error && data) {
        // Convert database milestones to timeline events
        const events: TimelineEvent[] = data.map((milestone: CareerMilestone) => {
          // Format date range
          const startYear = new Date(milestone.startDate).getFullYear();
          const endYear = milestone.isCurrent ? 'Current' : milestone.endDate ? new Date(milestone.endDate).getFullYear() : '';
          const yearRange = endYear ? `${startYear}-${endYear}` : startYear.toString();

          // Extract achievements
          const achievements = milestone.achievements?.map(a => a.achievementTitle || a.description || '') || [];

          return {
            year: yearRange,
            title: milestone.roleTitle,
            organization: milestone.companyName,
            description: milestone.description || '',
            achievements: achievements,
            type: milestone.milestoneType === 'promotion' ? 'milestone' : 
                  milestone.milestoneType === 'role_change' ? 'transition' : 'achievement'
          };
        });
        
        setTimelineEvents(events);
      }
    } catch (error) {
      console.error('Error loading career milestones:', error);
      setTimelineEvents([]);
    } finally {
      setLoading(false);
    }
  };

  const toggleSkill = (skill: string) => {
    setExpandedSkill(expandedSkill === skill ? null : skill);
  };

  const heroData = {
    name: "Kendal Thompson",
    title: "Laboratory Director & Operations Manager",
    tagline: "Transforming laboratory operations into competitive business advantages through regulatory excellence, team development, and innovative process optimization.",
    image: "/assets/images/20190108_061456-1770566418822.jpg",
    imageAlt: "Professional woman in business attire standing confidently in modern laboratory setting with scientific equipment in background"
  };

  const missionData = {
    mission: "To elevate laboratory operations from cost centers to strategic assets by combining technical expertise with business acumen, driving measurable results in highly regulated environments.",
    vision: "To be recognized as the go-to leader for organizations seeking transformative laboratory leadership that bridges scientific precision with operational excellence.",
    approach: "Data-driven decision making paired with people-first leadership, ensuring regulatory compliance enables business growth while fostering high-performing teams."
  };

  const philosophyPillars: PhilosophyPillar[] = [
  {
    icon: "ShieldCheckIcon",
    title: "Regulatory Excellence as Business Enabler",
    description: "Compliance is not a barrier but a competitive advantage that builds trust and opens opportunities.",
    principles: [
    "Proactive compliance strategies that prevent issues before they arise",
    "Turning regulatory requirements into operational efficiencies",
    "Building quality systems that support business growth",
    "Creating cultures where compliance is everyone's responsibility"]

  },
  {
    icon: "UsersIcon",
    title: "People-Centered Leadership",
    description: "High-performing teams are built on trust, development, and clear communication.",
    principles: [
    "Investing in continuous training and professional development",
    "Creating psychological safety for innovation and problem-solving",
    "Recognizing and leveraging individual strengths",
    "Building collaborative environments that drive collective success"]

  },
  {
    icon: "ChartBarIcon",
    title: "Data-Driven Decision Making",
    description: "Strategic decisions backed by metrics, analytics, and measurable outcomes.",
    principles: [
    "Implementing KPIs that align with business objectives",
    "Using data to identify improvement opportunities",
    "Transparent reporting that builds stakeholder confidence",
    "Continuous monitoring and adjustment based on results"]

  },
  {
    icon: "LightBulbIcon",
    title: "Continuous Innovation",
    description: "Embracing technology and process improvements to stay ahead of industry demands.",
    principles: [
    "Evaluating and implementing cutting-edge laboratory technologies",
    "Streamlining workflows to reduce turnaround times",
    "Fostering a culture of continuous improvement",
    "Balancing innovation with operational stability"]

  }];


  const coreValues: CoreValue[] = [
  {
    icon: "ScaleIcon",
    title: "Integrity",
    description: "Unwavering commitment to ethical practices, transparency, and doing what's right even when no one is watching.",
    color: "bg-primary"
  },
  {
    icon: "AcademicCapIcon",
    title: "Excellence",
    description: "Pursuing the highest standards in every aspect of laboratory operations, from technical accuracy to customer service.",
    color: "bg-secondary"
  },
  {
    icon: "UserGroupIcon",
    title: "Collaboration",
    description: "Fostering teamwork and open communication to leverage collective expertise and achieve shared goals.",
    color: "bg-accent"
  },
  {
    icon: "SparklesIcon",
    title: "Innovation",
    description: "Embracing new technologies and methodologies to continuously improve efficiency and service quality.",
    color: "bg-warning"
  },
  {
    icon: "HeartIcon",
    title: "Accountability",
    description: "Taking ownership of results, learning from challenges, and maintaining responsibility at all levels.",
    color: "bg-error"
  },
  {
    icon: "TrophyIcon",
    title: "Results-Driven",
    description: "Focusing on measurable outcomes that deliver value to clients, stakeholders, and team members.",
    color: "bg-success"
  }];

  const endorsements: Endorsement[] = [
  {
    skill: "Laboratory Operations Management",
    count: 24,
    endorsers: [
    {
      name: "Dr. Sarah Mitchell",
      title: "Director of Quality Assurance, PharmaTech Solutions",
      image: "https://img.rocket.new/generatedImages/rocket_gen_img_18811c304-1763296452128.png",
      alt: "Professional woman with blonde hair in business attire smiling at camera"
    },
    {
      name: "James Rodriguez",
      title: "VP of Laboratory Services, EnviroTest Corp",
      image: "https://img.rocket.new/generatedImages/rocket_gen_img_104e32cd5-1763293338887.png",
      alt: "Hispanic man with beard wearing navy suit and tie in office setting"
    },
    {
      name: "Dr. Emily Chen",
      title: "Chief Scientific Officer, BioAnalytics Inc",
      image: "https://img.rocket.new/generatedImages/rocket_gen_img_17064d52c-1766741247223.png",
      alt: "Asian woman with glasses in white lab coat holding clipboard"
    }]

  },
  {
    skill: "Regulatory Compliance & Quality Assurance",
    count: 31,
    endorsers: [
    {
      name: "Michael Thompson",
      title: "Compliance Manager, Global Testing Services",
      image: "https://img.rocket.new/generatedImages/rocket_gen_img_1f23d9c3d-1763295425663.png",
      alt: "African American man in gray suit with confident expression"
    },
    {
      name: "Dr. Lisa Anderson",
      title: "Quality Director, Precision Laboratories",
      image: "https://img.rocket.new/generatedImages/rocket_gen_img_128683b30-1763296674609.png",
      alt: "Professional woman with brown hair in business casual attire"
    }]

  },
  {
    skill: "Team Leadership & Development",
    count: 19,
    endorsers: [
    {
      name: "Robert Martinez",
      title: "HR Director, LabCorp Regional",
      image: "https://images.unsplash.com/photo-1695830209166-161b6297541d",
      alt: "Man with short dark hair in blue button-down shirt smiling"
    },
    {
      name: "Jennifer Park",
      title: "Operations Manager, Analytical Services Group",
      image: "https://img.rocket.new/generatedImages/rocket_gen_img_10deca2fb-1763299994309.png",
      alt: "Asian woman with long black hair in professional blazer"
    }]

  }];

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary via-primary/95 to-secondary">
      <MissionStatement {...missionData} />
      {/* Career Journey Timeline */}
      {loading ? (
        <div className="py-16 lg:py-24">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="animate-pulse space-y-8">
              <div className="h-12 bg-muted rounded-lg w-1/3 mx-auto" />
              <div className="space-y-6">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="h-32 bg-muted rounded-xl" />
                ))}
              </div>
            </div>
          </div>
        </div>
      ) : (
        <CareerTimeline events={timelineEvents} />
      )}
      
      <CoreValues values={coreValues} />
      <CallToAction />
    </div>);

};

export default AboutInteractive;